// The player's persistent profile and every rule that changes it: coins, XP/levels, incubators, the Waiting shelf,
// opening capsules into Pals, Pal income while away, cosmetics, daily orders, the 7-day streak, rewarded-ad budget and
// first-time flags. Pure functions over a plain object with an injected clock, so tools/meta_test.ts can replay days.
// Persistence lives in store.ts (CrazyGames data module when hosted, localStorage otherwise).

import {
  RARITY, FIRST_CAPSULE_SEC, INCUBATORS_START, INCUBATOR_PRICES, WAITING_MAX, OPEN_NOW_ADS_PER_DAY, PAL_LEVEL_INCOME,
  AWAY_CAP_H, COIN, XP, LEVEL_MAX, xpForLevel, DAILY_ORDERS, STREAK, PARADE_COIN_MULT,
} from "../../../shared/tuning";
import { PALS, COSMETICS, DEFAULT_EQUIP, ORDER_POOL, type Rarity, type CosKind, type OrderDef } from "./catalog";

export const PROFILE_V = 1;

export interface Incubator { rarity: Rarity; start: number; dur: number; }   // ms epoch, ms
export interface OrderState { id: string; prog: number; done: boolean; }
export interface Profile {
  v: number;
  pid: string;                    // random id: the weekly board + cloud copy key
  name: string;
  coins: number;
  xp: number;                     // xp inside the current level
  level: number;
  slots: number;                  // incubator count
  incub: (Incubator | null)[];
  waiting: Rarity[];
  pals: Record<string, { n: number; lvl: number }>;
  owned: string[];                // cosmetic ids
  equip: Record<CosKind, string>;
  lastSeen: number;               // ms epoch of the last income collection
  daily: { day: string; orders: OrderState[]; bonusClaimed: boolean };
  streak: { idx: number; last: string };   // idx = next reward index 0..6; last = UTC day claimed
  ads: { day: string; n: number };
  stats: { rounds: number; wins: number; stashes: number; catches: number; perfects: number; misses: number; pops: number; bestStreakDays: number };
  flags: Record<string, number>; // first-time beats (onboarding), value = ms epoch seen
  firstCapsule: boolean;          // the 90-s capsule has been given
}

export const utcDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const rnd = () => Math.random().toString(36).slice(2, 12);

export function freshProfile(now: number): Profile {
  return {
    v: PROFILE_V, pid: "p" + rnd(), name: "", coins: 0, xp: 0, level: 1,
    slots: INCUBATORS_START, incub: Array(INCUBATORS_START).fill(null), waiting: [], pals: {},
    owned: COSMETICS.filter((c) => c.price === 0 && c.level <= 1).map((c) => c.id), equip: { ...DEFAULT_EQUIP },
    lastSeen: now, daily: { day: "", orders: [], bonusClaimed: false }, streak: { idx: 0, last: "" }, ads: { day: "", n: 0 },
    stats: { rounds: 0, wins: 0, stashes: 0, catches: 0, perfects: 0, misses: 0, pops: 0, bestStreakDays: 0 },
    flags: {}, firstCapsule: false,
  };
}

/** repair anything a stale/corrupt save is missing; never throws */
export function migrate(raw: any, now: number): Profile {
  const p = freshProfile(now);
  if (!raw || typeof raw !== "object") return p;
  const out: Profile = { ...p, ...raw, stats: { ...p.stats, ...(raw.stats || {}) }, equip: { ...p.equip, ...(raw.equip || {}) },
    daily: { ...p.daily, ...(raw.daily || {}) }, streak: { ...p.streak, ...(raw.streak || {}) }, ads: { ...p.ads, ...(raw.ads || {}) },
    flags: { ...(raw.flags || {}) }, pals: { ...(raw.pals || {}) } };
  out.v = PROFILE_V;
  out.coins = Math.max(0, Math.floor(+out.coins || 0));
  out.level = Math.min(LEVEL_MAX, Math.max(1, Math.floor(+out.level || 1)));
  out.slots = Math.min(INCUBATORS_START + INCUBATOR_PRICES.length, Math.max(INCUBATORS_START, Math.floor(+out.slots || 0)));
  out.incub = Array.from({ length: out.slots }, (_, i) => (Array.isArray(raw.incub) && raw.incub[i] && raw.incub[i].rarity ? raw.incub[i] : null));
  out.waiting = Array.isArray(raw.waiting) ? raw.waiting.filter((r: any) => RARITY.some((x) => x.id === r)).slice(0, WAITING_MAX) : [];
  const owned = new Set<string>(Array.isArray(raw.owned) ? raw.owned : []);
  for (const id of p.owned) owned.add(id);
  out.owned = [...owned].filter((id) => COSMETICS.some((c) => c.id === id));
  for (const k of Object.keys(out.equip) as CosKind[]) if (!out.owned.includes(out.equip[k])) out.equip[k] = DEFAULT_EQUIP[k];
  if (typeof out.pid !== "string" || !/^p[a-z0-9]{6,}$/.test(out.pid)) out.pid = p.pid;
  return out;
}

/** merge two copies of one player (cloud vs local, guest vs account): never lose progress */
export function mergeProfiles(a: Profile, b: Profile): Profile {
  const [hi, lo] = a.level * 1e9 + a.xp >= b.level * 1e9 + b.xp ? [a, b] : [b, a];
  const out: Profile = JSON.parse(JSON.stringify(hi));
  out.coins = Math.max(a.coins, b.coins);
  for (const [id, s] of Object.entries(lo.pals)) { const m = out.pals[id]; out.pals[id] = m ? { n: Math.max(m.n, s.n), lvl: Math.max(m.lvl, s.lvl) } : s; }
  out.owned = [...new Set([...a.owned, ...b.owned])];
  for (const k of Object.keys(out.stats) as (keyof Profile["stats"])[]) out.stats[k] = Math.max(a.stats[k], b.stats[k]);
  for (const [k, v] of Object.entries(lo.flags)) if (!out.flags[k]) out.flags[k] = v;
  out.firstCapsule = a.firstCapsule || b.firstCapsule;
  return out;
}

// ---------------------------------------------------------------- levels
export function addXp(p: Profile, n: number): number[] {
  const ups: number[] = [];
  p.xp += Math.max(0, Math.round(n));
  while (p.level < LEVEL_MAX && p.xp >= xpForLevel(p.level)) {
    p.xp -= xpForLevel(p.level); p.level++; ups.push(p.level);
    p.coins += 50 + 10 * p.level;                                   // every level pays
    for (const c of COSMETICS) if (c.price === 0 && c.level === p.level && !p.owned.includes(c.id)) p.owned.push(c.id);
  }
  if (p.level >= LEVEL_MAX) p.xp = 0;
  return ups;
}
export const levelUnlocks = (lvl: number) => COSMETICS.filter((c) => c.price === 0 && c.level === lvl);

// ---------------------------------------------------------------- capsules, incubators, Pals
export function rollRarity(rng: () => number = Math.random): Rarity {
  const tot = RARITY.reduce((s, r) => s + r.w, 0); let x = rng() * tot;
  for (const r of RARITY) { x -= r.w; if (x < 0) return r.id; }
  return "common";
}
export const openSecOf = (r: Rarity) => RARITY.find((x) => x.id === r)!.openSec;

/** a capsule arrives (stash, order bonus, streak): incubator if free, else the shelf. Returns where it went. */
export function addCapsule(p: Profile, rarity: Rarity, now: number): "incubator" | "waiting" | "full" {
  const free = p.incub.findIndex((x) => !x);
  if (free >= 0) {
    const dur = (!p.firstCapsule ? FIRST_CAPSULE_SEC : openSecOf(rarity)) * 1000;
    p.firstCapsule = true;
    p.incub[free] = { rarity, start: now, dur };
    return "incubator";
  }
  if (p.waiting.length < WAITING_MAX) { p.waiting.push(rarity); return "waiting"; }
  return "full";
}
export const remainingMs = (inc: Incubator, now: number) => Math.max(0, inc.start + inc.dur - now);
export const isReady = (inc: Incubator | null, now: number) => !!inc && remainingMs(inc, now) === 0;

export interface OpenResult { pal: string; rarity: Rarity; dup: boolean; level: number; coins: number; }
/** open a ready incubator (or `force` = the rewarded "OPEN NOW") → a Pal of that rarity; refill from the shelf */
export function openIncubator(p: Profile, i: number, now: number, force = false, rng: () => number = Math.random): OpenResult | null {
  const inc = p.incub[i];
  if (!inc || (!force && !isReady(inc, now))) return null;
  const pool = PALS.filter((x) => x.rarity === inc.rarity);
  const pal = pool[Math.floor(rng() * pool.length) % pool.length];
  const had = p.pals[pal.id];
  if (had) { had.n++; had.lvl = palLevelFor(had.n); } else p.pals[pal.id] = { n: 1, lvl: 1 };
  const coins = { common: 20, rare: 60, epic: 200, legendary: 800 }[inc.rarity];
  p.coins += coins;
  p.incub[i] = null;
  if (p.waiting.length) { const r = p.waiting.shift()!; p.incub[i] = { rarity: r, start: now, dur: openSecOf(r) * 1000 }; }
  return { pal: pal.id, rarity: inc.rarity, dup: !!had, level: p.pals[pal.id].lvl, coins };
}
/** duplicates level a Pal: 1 copy = L1, 3 = L2, 6 = L3, 10 = L4, 15 = L5 */
export function palLevelFor(n: number) { let l = 1, need = 3, step = 3; while (n >= need && l < 5) { l++; need += step; step++; } return l; }

export function palIncomePerHour(p: Profile): number {
  let s = 0;
  for (const [id, st] of Object.entries(p.pals)) {
    const pal = PALS.find((x) => x.id === id); if (!pal) continue;
    s += RARITY.find((r) => r.id === pal.rarity)!.income * (1 + PAL_LEVEL_INCOME * (st.lvl - 1));
  }
  return Math.round(s);
}
/** collect Pal income since lastSeen (capped at AWAY_CAP_H) */
export function collectIncome(p: Profile, now: number): number {
  const h = Math.min(AWAY_CAP_H, Math.max(0, (now - p.lastSeen) / 3.6e6));
  const got = Math.floor(palIncomePerHour(p) * h);
  p.lastSeen = now; p.coins += got;
  return got;
}

export function buyIncubator(p: Profile, now: number): boolean {
  const k = p.slots - INCUBATORS_START; const price = INCUBATOR_PRICES[k];
  if (price === undefined || p.coins < price) return false;
  p.coins -= price; p.slots++;
  const r = p.waiting.shift();                     // the new slot takes the oldest capsule on the shelf at once
  p.incub.push(r ? { rarity: r, start: now, dur: openSecOf(r) * 1000 } : null);
  return true;
}
export const nextIncubatorPrice = (p: Profile) => INCUBATOR_PRICES[p.slots - INCUBATORS_START];

// ---------------------------------------------------------------- cosmetics
export function buyCosmetic(p: Profile, id: string): boolean {
  const c = COSMETICS.find((x) => x.id === id);
  if (!c || p.owned.includes(id) || c.price <= 0 || p.coins < c.price) return false;
  p.coins -= c.price; p.owned.push(id); p.equip[c.kind] = id; return true;
}
export function equip(p: Profile, id: string): boolean {
  const c = COSMETICS.find((x) => x.id === id);
  if (!c || !p.owned.includes(id)) return false;
  p.equip[c.kind] = id; return true;
}

// ---------------------------------------------------------------- the rewarded "OPEN NOW" budget
export function adsLeft(p: Profile, now: number) { const d = utcDay(now); if (p.ads.day !== d) { p.ads = { day: d, n: 0 }; } return OPEN_NOW_ADS_PER_DAY - p.ads.n; }
export function spendAd(p: Profile, now: number) { if (adsLeft(p, now) <= 0) return false; p.ads.n++; return true; }

// ---------------------------------------------------------------- daily orders
function hash(s: string) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
/** today's three orders: seeded by profile + UTC day, never two of the same stat */
export function ensureDaily(p: Profile, now: number) {
  const day = utcDay(now);
  if (p.daily.day === day && p.daily.orders.length) return;
  let h = hash(p.pid + day); const picked: OrderDef[] = []; const used = new Set<string>();
  for (let guard = 0; picked.length < DAILY_ORDERS && guard < 200; guard++) {
    h = Math.imul(h ^ (h >>> 13), 2246822519) >>> 0;
    const o = ORDER_POOL[h % ORDER_POOL.length];
    if (used.has(o.stat)) continue;
    used.add(o.stat); picked.push(o);
  }
  p.daily = { day, orders: picked.map((o) => ({ id: o.id, prog: 0, done: false })), bonusClaimed: false };
}
export const orderDef = (id: string) => ORDER_POOL.find((o) => o.id === id)!;

// ---------------------------------------------------------------- the 7-day streak (a missed day pauses, never resets)
export type StreakReward = { coins: number } | { capsule: Rarity };
export function streakDue(p: Profile, now: number) { return p.streak.last !== utcDay(now); }
export function claimStreak(p: Profile, now: number): StreakReward | null {
  if (!streakDue(p, now)) return null;
  const r = STREAK[p.streak.idx % STREAK.length];
  p.streak = { idx: (p.streak.idx + 1) % STREAK.length, last: utcDay(now) };
  p.stats.bestStreakDays = Math.max(p.stats.bestStreakDays, p.streak.idx === 0 ? 7 : p.streak.idx);
  if (typeof r === "number") { p.coins += r; return { coins: r }; }
  addCapsule(p, r as Rarity, now);
  return { capsule: r as Rarity };
}

// ---------------------------------------------------------------- a round's result → the profile
export interface RoundResult {
  role: "sneak" | "inspector";
  won: boolean;
  stashed: Rarity[];        // rarities rolled server-side for my stashes (golden = "legendary")
  perfects: number;
  catches: number;
  innocentPops: number;
  popped: number;           // times I was caught
  ignoredWhistles: number;
  parade: boolean;
}
export interface RoundPayout { coins: number; xp: number; levelUps: number[]; capsules: { rarity: Rarity; to: string }[]; ordersDone: string[]; ordersBonus: boolean; }
export function applyRound(p: Profile, r: RoundResult, now: number): RoundPayout {
  ensureDaily(p, now);
  const mult = r.parade ? PARADE_COIN_MULT : 1;
  let coins = (r.stashed.length * COIN.stash + r.perfects * COIN.perfect + r.catches * COIN.catch + (r.won ? COIN.win : 0)) * mult;
  const xp = XP.round + r.stashed.length * XP.stash + r.perfects * XP.perfect + r.catches * XP.catch + (r.won ? XP.win : 0);
  p.coins += coins;
  const levelUps = addXp(p, xp);
  const capsules = r.stashed.map((rarity) => ({ rarity, to: addCapsule(p, rarity, now) }));
  const s = p.stats;
  s.rounds++; if (r.won) s.wins++; s.stashes += r.stashed.length; s.catches += r.catches; s.perfects += r.perfects; s.misses += r.innocentPops;
  // orders
  const gain: Record<OrderDef["stat"], number> = {
    stash: r.stashed.length, perfect: r.perfects, catch: r.catches, win: r.won ? 1 : 0,
    clean: r.role === "sneak" && r.popped === 0 ? 1 : 0, ignored: r.ignoredWhistles,
  };
  const ordersDone: string[] = [];
  for (const o of p.daily.orders) {
    if (o.done) continue;
    const d = orderDef(o.id); o.prog = Math.min(d.goal, o.prog + gain[d.stat]);
    if (o.prog >= d.goal) { o.done = true; ordersDone.push(o.id); p.coins += 100; coins += 100; }
  }
  let ordersBonus = false;
  if (!p.daily.bonusClaimed && p.daily.orders.length && p.daily.orders.every((o) => o.done)) {
    p.daily.bonusClaimed = true; ordersBonus = true; capsules.push({ rarity: "rare", to: addCapsule(p, "rare", now) });
  }
  return { coins, xp, levelUps, capsules, ordersDone, ordersBonus };
}

/** first-time beats: true exactly once per flag */
export function firstTime(p: Profile, flag: string, now: number): boolean { if (p.flags[flag]) return false; p.flags[flag] = now; return true; }
